import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";
import { describeRejection, firstTimeOnPage, isUninformative } from "@/lib/errorReport";

// Errors that don't originate from our own code aren't actionable and just
// create Slack noise. Drop anything thrown from a browser extension or a
// third-party analytics/pixel script (e.g. ad-blockers killing their fetch).
const NOISE_PATTERNS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "apollo.io",
  "googletagmanager.com",
  "google-analytics.com",
  "doubleclick.net",
  "clarity.ms",
  "connect.facebook.net",
  "hs-scripts.com",
  "hs-analytics.net",
  "hsforms.net",
];

// A browser's whole message when a request never got an answer: the visitor's
// connection dropped, or they left the page while it loaded. Matched exactly,
// so "Failed to fetch dynamically imported module" (a real deploy problem)
// is still reported. Our own API being down is caught server-side instead.
const NETWORK_FAILURES = new Set([
  "failed to fetch",
  "load failed",
  "networkerror when attempting to fetch resource.",
  "the network connection was lost.",
  "the internet connection appears to be offline.",
]);

function isNoiseError(message: string, stack?: string): boolean {
  if (NETWORK_FAILURES.has(message.trim().toLowerCase())) return true;
  const haystack = `${message}\n${stack ?? ""}`.toLowerCase();
  return NOISE_PATTERNS.some((p) => haystack.includes(p));
}

function reportError(message: string, stack?: string) {
  if (isNoiseError(message, stack)) return; // third-party / extension noise
  if (isUninformative(message)) return; // nothing to act on
  if (!firstTimeOnPage(message)) return; // one alert per error per page, not a burst
  try {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => {}); // silent fail — never block the user
  } catch {
    // noop
  }
}

// Global unhandled promise rejections (e.g. failed API calls, async crashes)
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    const msg = describeRejection(e.reason);
    const stack = e.reason instanceof Error ? e.reason.stack : undefined;
    reportError(msg, stack);
  });
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    reportError(error.message, error.stack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
              <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                {this.state.error?.stack}
              </pre>
            </div>

            <button type="button"
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
